'use client';

import React from 'react';
import { Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isPlatformHub } from '@/constants/common';
import { useAuthenticationContext } from '@/context/authenticationProvider';
import { useCreditUsage } from '../../hooks/useCreditUsage';
import { useOpenSkyaiPlans } from '../../skyai-plans';
import {
  creditLevel,
  formatMeterLabel,
  meterFillPercent,
  type CreditLevel,
} from '../../utils/creditUsage';

const FILL_COLOR: Record<CreditLevel, string> = {
  ok: 'var(--primary-color)',
  low: '#E6B53B',
  exhausted: '#d0342c',
};

export default function CreditUsageMeter() {
  const { getAuthenticatedToken } = useAuthenticationContext();
  const usage = useCreditUsage(getAuthenticatedToken);
  const openPlans = useOpenSkyaiPlans();
  const { t, i18n } = useTranslation('ai-assistant');

  if (!usage) return null;

  const level = creditLevel(usage.used, usage.limit);

  return (
    <Box sx={{ mb: '0.5rem' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '0.5rem',
          fontSize: '0.75rem',
          color: 'var(--sw-white-color-90)',
        }}
      >
        <Box component="span" sx={{ opacity: level === 'ok' ? 0.6 : 0.9 }}>
          {formatMeterLabel(usage.used, usage.limit, { t, language: i18n.language })}
        </Box>
        {isPlatformHub && level !== 'ok' && (
          <Link
            component="button"
            type="button"
            onClick={openPlans}
            underline="hover"
            sx={{ fontSize: '0.75rem', color: 'var(--primary-color)' }}
          >
            {t('getMoreCredits')}
          </Link>
        )}
      </Box>
      <Box
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={usage.limit}
        aria-valuenow={Math.min(usage.used, usage.limit)}
        sx={{
          mt: '4px',
          height: '3px',
          borderRadius: '2px',
          bgcolor: 'rgba(244, 246, 255, 0.08)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${meterFillPercent(usage.used, usage.limit)}%`,
            bgcolor: FILL_COLOR[level],
            opacity: level === 'ok' ? 0.6 : 1,
          }}
        />
      </Box>
    </Box>
  );
}
